window.theme = window.theme || {};

theme.config = {
  mqlSmall: false,
  mediaQuerySmall: 'screen and (max-width: 749px)',
  isTouch: ('ontouchstart' in window) || (window.navigator.maxTouchPoints > 0),
  rtl: document.documentElement.getAttribute('dir') === 'rtl'
};

theme.initWhenVisible = function(options) {
  const threshold = options.threshold || 0;
  const observer = new IntersectionObserver((entries, observer) => {
    entries.forEach(entry => {
      if (entry.isIntersecting && typeof options.callback === 'function') {
        options.callback();
        observer.unobserve(entry.target);
      }
    });
  }, { rootMargin: `0px 0px ${threshold}px 0px` });
  observer.observe(options.element);
};

(function() {
  const mql = window.matchMedia(theme.config.mediaQuerySmall);
  theme.config.mqlSmall = mql.matches;
  mql.onchange = (e) => {
    theme.config.mqlSmall = e.matches;
    document.dispatchEvent(new CustomEvent(e.matches ? 'matchSmall' : 'unmatchSmall'));
  };

  window.addEventListener('beforeunload', () => {
    document.body.classList.add('unloading');
  });

  // Single authoritative 'loaded' class setter
  function markLoaded() {
    document.body.classList.add('loaded');
  }

  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    markLoaded();
  } else {
    window.addEventListener('DOMContentLoaded', markLoaded, { once: true });
  }

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      document.body.classList.remove('unloading');
      markLoaded();
    }
  });

  function viewportHeight() {
    const platform = navigator?.userAgentData?.platform || navigator?.platform || '';
    if (/iPad|iPhone|iPod/.test(platform) || (platform === 'MacIntel' && navigator.maxTouchPoints > 1)) {
      document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    }
    setHeaderHeight();
  }

  /* Banzayak Fix: Debounce resize to prevent scroll lag on mobile */
  window.addEventListener('resize', debounce(viewportHeight, 150));
  window.addEventListener('DOMContentLoaded', viewportHeight, { once: true });

  if (document.body.getAttribute('data-page-rendering') !== null) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const el = entry.target;
          if (entry.intersectionRatio === 0) return;
          if (!el.markedVisible) {
            el.attributeStyleMap && el.attributeStyleMap.set('content-visibility', 'visible');
            el.markedVisible = true;
          }
        });
      },
      { rootMargin: '50px 0px 100px 0px' }
    );
    document.querySelectorAll('.shopify-section + .shopify-section').forEach((s) => observer.observe(s));
  }
})();

// Prevent vertical scroll while using flickity sliders
(function() {
  let isFlickity = false;
  let startCoords;

  document.body.addEventListener('touchstart', function(e) {
    if (!e.target.closest('.flickity-slider')) {
      isFlickity = false;
      return;
    }
    isFlickity = true;
    startCoords = { x: e.touches[0].pageX, y: e.touches[0].pageY };
  });

  document.body.addEventListener('touchmove', function(e) {
    if (isFlickity && e.cancelable && startCoords) {
      const dx = e.touches[0].pageX - startCoords.x;
      if (typeof Flickity !== 'undefined' && Math.abs(dx) > (Flickity.defaults?.dragThreshold || 3)) {
        e.preventDefault();
      }
    }
  }, { passive: false });
})();

/* --- UTILITY FUNCTIONS --- */

function isStorageSupported(type) {
  if (window.self !== window.top) return false;
  const testKey = 'beyours:test';
  const storage = type === 'session' ? window.sessionStorage : window.localStorage;
  try {
    storage.setItem(testKey, '1');
    storage.removeItem(testKey);
    return true;
  } catch (e) {
    return false;
  }
}

function checkScrollbar() {
  const rect = document.body.getBoundingClientRect();
  return Math.round(rect.left + rect.right) < window.innerWidth;
}

function setScrollbarWidth() {
  if (!checkScrollbar()) return;
  const el = document.createElement('span');
  el.className = 'modal-scrollbar-measure';
  document.body.appendChild(el);
  const width = el.getBoundingClientRect().width - el.clientWidth;
  document.body.removeChild(el);
  document.documentElement.style.setProperty('--scrollbar-width', `${width}px`);
}

function setHeaderHeight() {
  const header = document.getElementById('shopify-section-header');
  if (header) {
    document.documentElement.style.setProperty('--header-height', `${parseInt(header.getBoundingClientRect().bottom)}px`);
  }
}

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  const elements = getFocusableElements(container);
  const first = elements[0];
  const last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;
    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = () => {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = (event) => {
    if (event.code?.toUpperCase() !== 'TAB') return;
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);
  if (elementToFocus) elementToFocus.focus();
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);
  if (elementToFocus) elementToFocus.focus();
}

// Focus-visible: only polyfill if CSS support is missing
if (!CSS.supports('selector(:focus-visible)')) {
  const navKeys = ['ARROWUP','ARROWDOWN','ARROWLEFT','ARROWRIGHT','TAB','ENTER','SPACE','ESCAPE','HOME','END','PAGEUP','PAGEDOWN'];
  let currentFocusedElement = null;
  let mouseClick = false;

  window.addEventListener('keydown', (e) => {
    if (navKeys.includes(e.code?.toUpperCase())) mouseClick = false;
  });
  window.addEventListener('mousedown', () => { mouseClick = true; });
  window.addEventListener('focus', () => {
    if (currentFocusedElement) currentFocusedElement.classList.remove('focused');
    if (mouseClick) return;
    currentFocusedElement = document.activeElement;
    currentFocusedElement?.classList.add('focused');
  }, true);
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((v) => {
    v.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((v) => {
    v.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((v) => v.pause());
  document.querySelectorAll('product-model').forEach((m) => {
    if (m.modelViewerUI) m.modelViewerUI.pause();
  });
}

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': `application/${type}` }
  };
}

/* Shopify Common JS — postLink kept, IE polyfills removed */
window.Shopify = window.Shopify || {};

Shopify.postLink = function(path, options = {}) {
  const method = options.method || 'post';
  const params = options.parameters || {};
  const form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);
  Object.entries(params).forEach(([key, value]) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = key;
    input.value = value;
    form.appendChild(input);
  });
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

/* --- CORE INTERACTIVE COMPONENTS --- */

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.querySelectorAll('button').forEach(
      (btn) => btn.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  onButtonClick(event) {
    event.preventDefault();
    const prev = this.input.value;
    event.target.name === 'plus' ? this.input.stepUp() : this.input.stepDown();
    if (prev !== this.input.value) this.input.dispatchEvent(this.changeEvent);
  }
}
customElements.define('quantity-input', QuantityInput);

class MenuDrawer extends HTMLElement {
  constructor(props) {
    super();
    this.classes = props?.classes || {
      open: 'menu-drawer--open',
      opening: 'menu-drawer--opening',
      closing: 'menu-drawer--closing'
    };
    this.mainDetailsToggle = this.querySelector('details');
    this.querySelectorAll('summary').forEach(s => s.addEventListener('click', this.onSummaryClick.bind(this)));
    this.querySelectorAll('button').forEach(b => b.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onSummaryClick(event) {
    const summary = event.currentTarget;
    const details = summary.parentNode;
    const isOpen = details.hasAttribute('open');
    if (details === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event) : this.openMenuDrawer(summary);
    }
  }

  openMenuDrawer(summary) {
    setHeaderHeight();
    setScrollbarWidth();
    summary.setAttribute('aria-expanded', true);
    document.body.classList.add(this.classes.open);
  }

  closeMenuDrawer(event) {
    if (event !== undefined) {
      document.body.classList.remove(this.classes.open);
      this.mainDetailsToggle?.removeAttribute('open');
    }
  }

  onCloseButtonClick(event) {
    this.closeMenuDrawer(event);
  }
}
customElements.define('menu-drawer', MenuDrawer);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    if (!this.slider) return;
    theme.initWhenVisible({ element: this, callback: this.init.bind(this), threshold: 200 });
  }

  init() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    const prev = this.querySelector('button[name="previous"]');
    const next = this.querySelector('button[name="next"]');
    if (prev) prev.addEventListener('click', this.onButtonClick.bind(this));
    if (next) next.addEventListener('click', this.onButtonClick.bind(this));
  }

  onButtonClick(event) {
    event.preventDefault();
    const itemWidth = this.sliderItems[0]?.clientWidth || 300;
    this.slider.scrollBy({
      left: event.currentTarget.name === 'next' ? itemWidth : -itemWidth,
      behavior: 'smooth'
    });
  }
}
customElements.define('slider-component', SliderComponent);

class UseAnimate extends HTMLElement {
  constructor() {
    super();
    this.setAttribute('animate', '');
  }
}
customElements.define('use-animate', UseAnimate);

/* --- MINI-CART AJAX CONTENT LOADER --- */

class MiniCart extends HTMLElement {
  constructor() {
    super();
    this.isFetching = false;
  }

  connectedCallback() {
    if (!this.querySelector('#cart')) this.loadCart();
  }

  loadCart() {
    if (this.isFetching) return;
    this.isFetching = true;
    const url = `${window.routes?.root_url || ''}?section_id=mini-cart`.replace('//', '/');
    fetch(url)
      .then((r) => r.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const content = doc.querySelector('#cart') || doc.querySelector('.mini-cart');
        if (content) { this.innerHTML = content.outerHTML; this.initCartEvents(); }
      })
      .catch((e) => console.error(e))
      .finally(() => { this.isFetching = false; });
  }

  getSectionsToRender() {
    return [{ id: 'mini-cart', section: 'mini-cart', selector: '#cart' }];
  }

  renderContents(parsedState) {
    if (parsedState?.sections?.['mini-cart']) {
      const doc = new DOMParser().parseFromString(parsedState.sections['mini-cart'], 'text/html');
      const content = doc.querySelector('#cart') || doc.querySelector('.mini-cart');
      if (content) { this.innerHTML = content.outerHTML; this.initCartEvents(); }
    } else {
      this.loadCart();
    }

    // Auto open drawer
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer?.open) cartDrawer.open();

    // Update count bubbles from parsedState — no extra fetch needed
    const itemCount = parsedState?.item_count ?? parsedState?.items_count ?? null;
    if (itemCount !== null) {
      document.querySelectorAll('.cart-count-bubble').forEach((bubble) => {
        if (itemCount > 0) {
          bubble.classList.remove('hidden');
          bubble.style.display = 'inline-flex';
          const span = bubble.querySelector('span:not(.visually-hidden)');
          if (span) span.textContent = itemCount;
        } else {
          bubble.style.display = 'none';
        }
      });
    }
  }

  initCartEvents() {
    this.querySelectorAll('cart-remove-button a, .delete-product').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const href = btn.getAttribute('href');
        if (href) fetch(href).then(() => this.loadCart()).catch(console.error);
      });
    });

    this.querySelectorAll('quantity-input input').forEach((input) => {
      input.addEventListener('change', () => {
        fetch(window.routes?.cart_change_url || '/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ line: input.dataset.index, quantity: input.value })
        }).then(() => this.loadCart()).catch(console.error);
      });
    });
  }
}
if (!customElements.get('mini-cart')) customElements.define('mini-cart', MiniCart);

/* --- BULLETPROOF CART DRAWER CONTROLLER --- */

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.details = this.querySelector('details');
    this.summary = this.querySelector('summary');
    this.miniCart = this.querySelector('mini-cart');
    if (!this.details || !this.summary) return;
    this.summary.addEventListener('click', this.onSummaryClick.bind(this));
    this.details.addEventListener('click', (e) => {
      if (e.target === this.details) this.close();
    });
    // Store bound ref for cleanup
    this._onKeydown = (e) => { if (e.key === 'Escape' && this.isOpen()) this.close(); };
  }

  connectedCallback() {
    document.addEventListener('keydown', this._onKeydown);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this._onKeydown);
  }

  isOpen() { return this.details.hasAttribute('open'); }

  onSummaryClick(e) {
    e.preventDefault();
    e.stopPropagation();
    this.isOpen() ? this.close() : this.open();
  }

  open() {
    setHeaderHeight();
    setScrollbarWidth();
    this.details.setAttribute('open', '');
    this.details.classList.add('menu-opening');
    this.summary.setAttribute('aria-expanded', 'true');
    document.body.classList.add('mini-cart--open');
    if (this.miniCart?.loadCart) this.miniCart.loadCart();
  }

  close() {
    this.details.classList.remove('menu-opening');
    this.details.classList.add('menu-closing');
    this.summary.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('mini-cart--open');
    setTimeout(() => {
      this.details.removeAttribute('open');
      this.details.classList.remove('menu-closing');
    }, 250);
  }
}
if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);

/* --- PRODUCT & VARIANT ENGINE --- */

class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('change', this.onVariantChange);
  }
  onVariantChange() {
    this.updateOptions();
    this.updateMasterId();
    this.toggleAddButton(true, '', false);
    if (!this.currentVariant) {
      this.toggleAddButton(true, '', true);
      return;
    }
    this.updateURL();
    this.updateVariantInput();
    this.toggleAddButton(
      !this.currentVariant.available,
      this.currentVariant.available ? '' : (window.variantStrings?.soldOut || 'Sold out'),
      true
    );
  }
  updateOptions() {
    this.options = Array.from(this.querySelectorAll('select, fieldset input:checked'), (el) => el.value);
  }

  updateMasterId() {
    this.currentVariant = this.getVariantData().find((variant) =>
      !variant.options.map((option, i) => this.options[i] === option).includes(false)
    );
  }

  updateURL() {
    if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
    window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

 updateVariantInput() {
    document.querySelectorAll(`#product-form-${this.dataset.section}`).forEach((form) => {
      const input = form.querySelector('input[name="id"]');
      if (input) {
        input.value = this.currentVariant.id;
        input.disabled = false;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
    this.updateMedia();
  }

  updateMedia() {
    if (!this.currentVariant?.featured_media) return;
    const mediaId = this.currentVariant.featured_media.id;
    const sectionId = this.dataset.section;

    // media-gallery custom element (stacked layout)
    const gallery = document.querySelector('media-gallery');
    if (gallery?.setActiveMedia) {
      gallery.setActiveMedia(`${sectionId}-${mediaId}`, true);
      return;
    }

    // Fallback: scroll to matching media item directly
    const mediaItem = document.querySelector(
      `[data-media-id="${sectionId}-${mediaId}"]`
    );
    if (mediaItem) {
      mediaItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }


  toggleAddButton(disable = true, text, modifyClass = true) {
    document.querySelectorAll(`#product-form-${this.dataset.section}`).forEach((form) => {
      const btn = form.querySelector('[name="add"]');
      if (!btn) return;
      if (disable) {
        btn.setAttribute('disabled', 'disabled');
        if (text) btn.textContent = text;
      } else {
        btn.removeAttribute('disabled');
        btn.textContent = window.variantStrings?.addToCart || 'Add to cart';
      }
    });
  }

  getVariantData() {
    this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
    return this.variantData;
  }
}
customElements.define('variant-selects', VariantSelects);

class VariantRadios extends VariantSelects {
  updateOptions() {
    this.options = Array.from(this.querySelectorAll('fieldset')).map((fieldset) =>
      Array.from(fieldset.querySelectorAll('input')).find((r) => r.checked)?.value
    );
  }
}
customElements.define('variant-radios', VariantRadios);

class ProductForm extends HTMLElement {
  constructor() {
    super();
    this.miniCart = document.querySelector('mini-cart');
    this.form = this.querySelector('form');
    if (!this.form) return;
    this.form.querySelectorAll('[name="id"]').forEach((input) => { input.disabled = false; });
    this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
  }

  onSubmitHandler(evt) {
    if (document.body.classList.contains('template-cart')) return;
    evt.preventDefault();
    const submitButton = this.querySelector('[type="submit"]');
    if (submitButton.classList.contains('loading')) return;
    const idInput = this.form.querySelector('[name="id"]');
    if (!idInput?.value) { alert('Please select an available variant.'); return; }
    idInput.disabled = false;
    submitButton.setAttribute('aria-disabled', true);
    submitButton.classList.add('loading');
    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];
    const formData = new FormData(this.form);
    if (this.miniCart?.getSectionsToRender) {
      formData.append('sections', this.miniCart.getSectionsToRender().map((s) => s.id));
    }
    formData.append('sections_url', window.location.pathname);
    config.body = formData;
    fetch(window.routes?.cart_add_url || '/cart/add.js', config)
      .then((r) => r.json())
      .then((response) => {
        if (response.status) { alert(response.description || response.message); return; }
        if (this.miniCart?.renderContents) {
          this.miniCart.renderContents(response);
        } else {
          window.location.href = window.routes?.cart_url || '/cart';
        }
      })
      .catch(console.error)
      .finally(() => {
        submitButton.classList.remove('loading');
        submitButton.removeAttribute('aria-disabled');
      });
  }
}
customElements.define('product-form', ProductForm);

class AddToCart extends HTMLElement {
  constructor() {
    super();
    this.miniCart = document.querySelector('mini-cart');
    this.addEventListener('click', this.onClickHandler.bind(this));
  }

  onClickHandler(e) {
    e.preventDefault();
    const variantId = this.dataset.variantId;
    if (!variantId) return;
    this.setAttribute('disabled', true);
    this.classList.add('loading');
    const sections = this.miniCart?.getSectionsToRender?.().map((s) => s.id) || [];
    const body = JSON.stringify({ id: variantId, quantity: 1, sections, sections_url: window.location.pathname });
    fetch(window.routes?.cart_add_url || '/cart/add.js', { ...fetchConfig('javascript'), body })
      .then((r) => r.json())
      .then((parsedState) => {
        if (this.miniCart?.renderContents) {
          this.miniCart.renderContents(parsedState);
        } else {
          window.location.href = window.routes?.cart_url || '/cart';
        }
      })
      .catch(console.error)
      .finally(() => {
        this.classList.remove('loading');
        this.removeAttribute('disabled');
      });
  }
}
customElements.define('add-to-cart', AddToCart);

/* ============================================================================
   Luxury Card Interactive Controller: 60FPS Smooth Scrubbing
============================================================================ */
class CardHoverMedia extends HTMLElement {
  constructor() {
    super();
    this.video = this.querySelector('.card__hover-video');
    this.slides = Array.from(this.querySelectorAll('.card__gallery-slide'));
    this.totalSlides = this.slides.length;
    this.hasVideo = this.dataset.hasVideo === 'true';
    this.currentSlideIndex = 0;
    this.cardRect = null;
    this.rafId = null;
    this.isWindowScrolling = false;
    this.scrollTimeout = null;
    this.cardWrapper = this.closest('.card-wrapper') || this;
    this.initEvents();
  }

  initEvents() {
    window.addEventListener('scroll', () => {
      this.isWindowScrolling = true;
      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = setTimeout(() => { this.isWindowScrolling = false; }, 100);
    }, { passive: true });

    if (!theme.config.isTouch) {
      this.cardWrapper.addEventListener('mouseenter', this.onMouseEnter.bind(this));
      this.cardWrapper.addEventListener('mousemove', this.onMouseMove.bind(this), { passive: true });
      this.cardWrapper.addEventListener('mouseleave', this.onMouseLeave.bind(this));
    } else if (this.video) {
      this.setupMobileObserver();
    }

    if (this.video) {
      this.video.addEventListener('playing', () => this.video.classList.add('is-active'));
      this.video.addEventListener('pause', () => this.video.classList.remove('is-active'));
    }
  }

  onMouseEnter() {
    this.cardRect = this.getBoundingClientRect();
    this.slides.forEach((img) => { if (img.loading === 'lazy') img.loading = 'eager'; });
    if (this.hasVideo && this.video) this.playVideo();
  }

  onMouseMove(e) {
    if (this.isWindowScrolling || this.totalSlides <= 1 || (this.hasVideo && this.video)) return;
    if (!this.cardRect || this.cardRect.width === 0) this.cardRect = this.getBoundingClientRect();
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.rafId = requestAnimationFrame(() => {
      const x = Math.max(0, Math.min(e.clientX - this.cardRect.left, this.cardRect.width));
      const targetIndex = Math.min(this.totalSlides - 1, Math.floor((x / this.cardRect.width) * this.totalSlides));
      if (targetIndex !== this.currentSlideIndex) this.setActiveSlide(targetIndex);
    });
  }

  onMouseLeave() {
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.cardRect = null;
    if (this.hasVideo && this.video) this.stopVideo();
    this.setActiveSlide(0);
  }

  setActiveSlide(index) {
    if (!this.slides[index] || index === this.currentSlideIndex) return;
    this.slides[this.currentSlideIndex]?.classList.remove('is-active');
    this.currentSlideIndex = index;
    this.slides[index]?.classList.add('is-active');
  }

  playVideo() {
    if (!this.video.src && this.video.dataset.src) this.video.src = this.video.dataset.src;
    this.video.play()?.catch(() => {});
  }

  stopVideo() {
    this.video.pause();
    this.video.currentTime = 0;
    this.video.classList.remove('is-active');
  }

  setupMobileObserver() {
    new IntersectionObserver((entries) => {
      entries.forEach((e) => { e.isIntersecting ? this.playVideo() : this.stopVideo(); });
    }, { threshold: 0.6 }).observe(this.cardWrapper);
  }
}
customElements.define('card-hover-media', CardHoverMedia);
